import { ReelFailReason, ReelStatus } from '../../types';

/** The three stages the wait screen shows: 1 analysing, 2 planning, 3 rendering. */
export type ReelStage = 1 | 2 | 3;

/** The statuses where the reel still works. The app polls only while the status is one of them. */
export const REEL_WORKING_STATUSES: ReadonlyArray<ReelStatus> = ['queued', 'analysing', 'planning', 'rendering'];

export function isReelWorking(status: ReelStatus): boolean {
  return REEL_WORKING_STATUSES.includes(status);
}

/** The stage of a working status (design b1: queued and analysing are stage 1). */
export function reelStageOfStatus(status: ReelStatus): ReelStage | null {
  switch (status) {
    case 'queued':
    case 'analysing':
      return 1;
    case 'planning':
      return 2;
    case 'rendering':
      return 3;
    case 'ready':
    case 'approved':
    case 'rejected':
    case 'failed':
      return null;
    default:
      return null;
  }
}

/**
 * The stage where a failed reel stopped (design b3). STALLED and an unknown reason stop at the
 * last stage the app saw; with no such stage, at stage 1.
 */
export function reelStageOfFailure(reason: ReelFailReason | null, lastSeenStage: ReelStage | null): ReelStage {
  switch (reason) {
    case 'SONG_TOO_SHORT':
    case 'UNUSABLE_AUDIO':
    case 'ANALYSIS_FAILED':
      return 1;
    case 'DIRECTOR_FAILED':
    case 'BUDGET_EXCEEDED':
    case 'AI_BUDGET_EXHAUSTED':
      return 2;
    case 'VOICEOVER_FAILED':
    case 'RENDER_FAILED':
    case 'RENDER_TIMEOUT':
      return 3;
    case 'STALLED':
    case null:
      return lastSeenStage ?? 1;
    default:
      return lastSeenStage ?? 1;
  }
}

/** What the main button of a failed reel does (design b4). */
export type ReelFailAction = 'tryAgain' | 'changeSong' | 'backToStudio' | 'upgrade';

export function reelFailAction(reason: ReelFailReason | null): ReelFailAction {
  switch (reason) {
    case 'SONG_TOO_SHORT':
    case 'UNUSABLE_AUDIO':
      return 'changeSong';
    case 'BUDGET_EXCEEDED':
      return 'backToStudio';
    case 'AI_BUDGET_EXHAUSTED':
      // The Free AI budget is spent: a new try fails the same way until an upgrade.
      return 'upgrade';
    case 'ANALYSIS_FAILED':
    case 'DIRECTOR_FAILED':
    case 'VOICEOVER_FAILED':
    case 'RENDER_FAILED':
    case 'RENDER_TIMEOUT':
    case 'STALLED':
    case null:
      return 'tryAgain';
    default:
      return 'tryAgain';
  }
}
